import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfig, AxiosError } from 'axios';

/** Gateway chính (`VITE_API_BASE_URL`) — không dùng cho prediction/simulation host riêng (`predictionSimulationApiClient`). */
import { apiClient } from '@/lib/api-client';
import type { ApiError } from '@/types/api';
import { extractAxiosResponseDataMessage } from '@/utils/getApiErrorMessage';

interface AxiosBaseQueryArgs {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  responseType?: AxiosRequestConfig['responseType'];
}

const axiosBaseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, ApiError> = async ({
  url,
  method = 'GET',
  data,
  params,
  headers,
  responseType,
}) => {
  try {
    const result = await apiClient({ url, method, data, params, headers, responseType });
    return { data: result.data };
  } catch (err) {
    const axiosError = err as AxiosError<Record<string, unknown>>;
    const response = axiosError.response;
    const data = response?.data;
    return {
      error: {
        status: response?.status ?? 0,
        message: extractAxiosResponseDataMessage(data, axiosError.message),
        errors:
          data && typeof data === 'object' && 'errors' in data
            ? (data.errors as Record<string, string[]>)
            : undefined,
        noResponse: !response,
      },
    };
  }
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery,
  tagTypes: [
    'Auth',
    'User',
    'Permissions',
    'Map',
    'Dashboard',
    'FloodPoint',
    'FloodForecast',
    'FloodHistory',
    'Alert',
    'Route',
    'EvacuationZone',
    'RescueStation',
    'ReportTemplate',
    'Report',
    'DashboardStats',
    'Weather',
    'AffectedArea',
    'HistoricalAnalysis',
    'YearlyComparison',
    'SeverityDistribution',
    'WaterLevelTrend',
    'RegionalStats',
    'Menu',
    'AdminUser',
    'AdminMenu',
    'MapLayer',
    'Catalog',
    'Basin',
    'Station',
    'NetcdfJob',
    'DataArtifact',
    'EtlJob',
    'AuditLog',
    'ReportSummary',
    'FloodJob',
    'OpsSetting',
    'OpsAlert',
    'StationAggregate',
    'RoleFeatureMatrix',
  ],
  endpoints: () => ({}),
});
